/**
 * Product review helpers.
 *
 * - Every product shows 3 to 4 reviews (count is picked deterministically per
 *   product, so it never changes between page loads).
 * - Real (admin-approved) reviews from the API are always shown first, newest
 *   first, and are never replaced; the list is only topped up with the
 *   sample reviews below until the target count (3 or 4) is reached.
 * - Every product gets a DIFFERENT set of sample reviews (names, wording,
 *   dates, star counts) - picked deterministically from the product's
 *   id/slug/name so the same product always shows the same sample reviews.
 * - The star rating shown next to the product name / in the reviews section
 *   both come from the same function (getProductRatingSummary) so they never
 *   disagree with each other.
 *
 * This file only ever ADDS filler reviews underneath real ones - it never
 * hides, edits or reorders a real, admin-approved review.
 */

export const MIN_REVIEWS = 3;
export const MAX_REVIEWS = 4;

const hash = (str) => {
  let h = 2166136261;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h >>> 0);
};

const productKey = (product) => `${product?.id ?? ""}|${product?.slug ?? ""}|${product?.name ?? ""}`;

/* ── Reviewer names ── */
const FIRST = [
  "Anitha", "Ramesh", "Kavya", "Suresh", "Divya", "Arvind", "Nandhini", "Vikram", "Shalini", "Gokul",
  "Preethi", "Mahesh", "Swathi", "Karthik", "Meenakshi", "Sundar", "Aishwarya", "Praveen", "Lavanya", "Bala",
  "Hema", "Sathya", "Roopa", "Dinesh", "Keerthana", "Mani", "Vasanthi", "Siva", "Nithya", "Yogesh",
  "Deepika", "Rajkumar", "Shanthi", "Aravind", "Indhu", "Magesh", "Vidya", "Senthilkumar", "Pavithra", "Manikandan",
];
const LAST = [
  "Murthy", "Varma", "Shetty", "Iyer", "Nair", "Pillai", "Reddy", "Chettiar", "Rajan", "Kannan",
  "Mohan", "Vel", "Pandian", "Subbiah", "Ananth", "Raghavan", "Elango", "Selvam", "Krishnamurthy", "Durai",
];

/* ── Review wording (product/medicine focused, not consultation focused) ── */
const FIVE_STAR_COMMENTS = [
  "Genuine product and very well packed. It arrived quickly and works exactly as described.",
  "This has made a real difference for me within a couple of weeks. Quality is excellent.",
  "Exactly as shown on the site. Fresh stock, proper sealed packaging and fast delivery.",
  "Very effective and no side effects so far. Will definitely reorder this one.",
  "Best quality I have used so far. Easy to use and the results are noticeable.",
  "Impressed with the packaging and freshness. Reached me two days before the expected date.",
  "Switched to this after trying a few other brands - this is clearly better quality.",
  "Great value for the price. The product matches the description perfectly.",
  "My whole family uses this now. Consistent quality every time we order.",
  "Fast shipping and the product itself is top notch. Highly recommend.",
  "Noticed good improvement after regular use. Packaging keeps it fresh and intact.",
  "Authentic product with a proper batch number and expiry printed clearly. Very satisfied.",
  "Works wonderfully and the taste/texture is pleasant, easy to continue daily.",
  "Ordered this a second time - same great quality as the first order.",
  "Very happy with this purchase, exactly what my doctor recommended.",
];

const FOUR_STAR_COMMENTS = [
  "Good quality product, does what it says. Delivery took a little longer than expected.",
  "Works well overall. Packaging was decent, just slightly bulky for shipping.",
  "Happy with the results so far, will continue using for a few more weeks.",
  "Nice product and reasonably priced. Would like a bigger pack size option.",
  "Effective product, noticed mild improvement within the first week itself.",
  "Good experience overall, only the outer box was a bit dented on arrival.",
  "Quality is good and consistent with my last order. Minor delay in delivery this time.",
  "Does the job well. Instructions on the label could be a little clearer.",
  "Satisfied with the purchase, seal and packaging were intact on delivery.",
  "Decent product for the price, results are gradual but steady.",
  "Good quality overall, I would buy again once the current pack is finished.",
];

const THREE_STAR_COMMENTS = [
  "Average experience. Product works but the effect is slower than I expected.",
  "Okay quality, packaging could be better as the box arrived slightly crushed.",
  "Fair product, does what is needed but nothing exceptional.",
  "Decent but delivery took longer than the estimated date on the order.",
  "It's fine for the price, though I expected a bit more noticeable effect by now.",
  "Reasonable product, label instructions were a little hard to follow at first.",
];

/* Rating patterns of length 3 or 4, all averaging between roughly 4.0 and 5.0
   since these are reviews from customers who completed a purchase. */
const PATTERNS_3 = [
  [5, 4, 5], [4, 5, 5], [5, 5, 4], [4, 4, 5], [5, 5, 5], [4, 5, 4], [5, 3, 5], [4, 4, 4],
];
const PATTERNS_4 = [
  [5, 4, 5, 4], [4, 5, 4, 5], [5, 5, 4, 4], [4, 4, 5, 5], [5, 5, 5, 4], [4, 5, 5, 3], [5, 4, 4, 5], [4, 4, 4, 5],
];

/* Days-ago offsets so each review has a different, recent-looking date. */
const DAY_OFFSETS = [3, 9, 18, 31, 47, 62, 85, 110, 140];

const pick = (arr, n) => arr[((n % arr.length) + arr.length) % arr.length];

/** How many sample reviews (3 or 4) this product should show, deterministic. */
export const sampleTargetCount = (product) => {
  const h = hash(productKey(product));
  return MIN_REVIEWS + (h % (MAX_REVIEWS - MIN_REVIEWS + 1));
};

/** Builds the sample reviews for one product (deterministic, 3 or 4 of them). */
export const buildSampleReviews = (product, count) => {
  if (!product) return [];
  const key = productKey(product);
  const h = hash(key);
  const target = count ?? sampleTargetCount(product);
  const ratings = (target === 4 ? PATTERNS_4 : PATTERNS_3)[h % (target === 4 ? PATTERNS_4.length : PATTERNS_3.length)];

  const out = [];
  const fiveCursor = h % FIVE_STAR_COMMENTS.length;
  const fourCursor = (h >> 3) % FOUR_STAR_COMMENTS.length;
  const threeCursor = (h >> 6) % THREE_STAR_COMMENTS.length;
  let five = 0, four = 0, three = 0;

  for (let i = 0; i < target; i++) {
    const rating = ratings[i] ?? 5;
    let comment;
    if (rating >= 5) {
      comment = pick(FIVE_STAR_COMMENTS, fiveCursor + five * 7);
      five++;
    } else if (rating === 4) {
      comment = pick(FOUR_STAR_COMMENTS, fourCursor + four * 5);
      four++;
    } else {
      comment = pick(THREE_STAR_COMMENTS, threeCursor + three * 4);
      three++;
    }

    const nameSeed = hash(`${key}#${i}`);
    const first = pick(FIRST, nameSeed);
    let last = pick(LAST, nameSeed >> 5);
    if (last === first) last = pick(LAST, (nameSeed >> 5) + 1);
    const reviewer = `${first} ${last}`;
    const days = pick(DAY_OFFSETS, i + (h % 3)) + (h % 5);
    const created = new Date(Date.now() - days * 24 * 60 * 60 * 1000);

    out.push({
      id: `sample-${product?.id ?? h}-${i}`,
      reviewer,
      rating,
      comment,
      created_at: created.toISOString(),
      isSample: true,
    });
  }
  return out;
};

/**
 * Real reviews first (unchanged, newest first), then sample reviews to reach
 * the product's target count (3 or 4).
 */
export const getProductReviewsWithSamples = (product, realReviews = []) => {
  const real = Array.isArray(realReviews) ? [...realReviews] : [];
  real.sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));

  if (!product) return real;
  const target = sampleTargetCount(product);
  if (real.length >= target) return real;

  const need = target - real.length;
  const samples = buildSampleReviews(product, target).slice(0, need);
  return [...real, ...samples];
};

/* Fallback shown for sample reviews and for real reviewers with no uploaded
   profile photo - keeps the existing look of the reviews section. */
export const DEFAULT_REVIEWER_AVATAR = "./front/assets/images/avatar.png";

/**
 * Reads the signed-in visitor's own account record the same way Myprofile.js
 * does (it's saved to localStorage on login/profile update as "user").
 */
const getLoggedInUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

const resolveImagePath = (raw, apiBaseUrl) => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("/")) return raw;
  return `${apiBaseUrl}public/profileimage/${raw}`;
};

/** Lowercase, letters-only - makes the name match ignore spacing/punctuation. */
const normName = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");

/**
 * Resolves the image to show for one review's reviewer.
 * - Sample (filler) reviews never carry a photo, so they always fall back.
 * - A real review coming back from the API may include the reviewer's own
 *   uploaded profile photo under one of a few possible keys depending on
 *   how the backend named/joined it; this checks all of them first.
 * - If the API doesn't send a photo for this review at all, but the review
 *   is the signed-in visitor's OWN review (matched by user id under several
 *   possible key names, or by name as a fallback), their own uploaded
 *   profile photo is used - the same one shown on their My Profile page.
 *   Backend-provided photos (the keys above) always win once the API sends
 *   one; this is only a stand-in until it does.
 * - A value that is already a full URL is used as-is; a bare filename is
 *   resolved the same way Myprofile.js resolves `profileimage` (served from
 *   `public/profileimage/<filename>`).
 */
export const getReviewerAvatarUrl = (review, apiBaseUrl = "") => {
  if (!review || review.isSample) return DEFAULT_REVIEWER_AVATAR;

  const apiProvided =
    review.profileimage ||
    review.profile_image ||
    review.user_image ||
    review.reviewer_image ||
    review.reviewer_profile ||
    review.avatar ||
    review.image ||
    review.photo ||
    review.user?.profileimage ||
    review.user?.profile_image ||
    review.user?.avatar ||
    review.user?.image ||
    null;

  const apiUrl = resolveImagePath(apiProvided, apiBaseUrl);
  if (apiUrl) return apiUrl;

  const me = getLoggedInUser();
  if (me && me.profileimage) {
    const myId = localStorage.getItem("user_id");
    const reviewUserId =
      review.user_id ?? review.userid ?? review.userId ?? review.uid ?? review.user?.id ?? null;
    const sameId = reviewUserId != null && myId != null && String(reviewUserId) === String(myId);
    const sameName =
      !sameId &&
      review.reviewer &&
      me.name &&
      normName(review.reviewer) === normName(me.name) &&
      normName(review.reviewer).length > 0;

    if (sameId || sameName) {
      return resolveImagePath(me.profileimage, apiBaseUrl) || DEFAULT_REVIEWER_AVATAR;
    }
  }

  return DEFAULT_REVIEWER_AVATAR;
};

/**
 * Deterministic static fallback rating (3.0-4.0) for a product with no real
 * reviews yet - per the "Default Product Star Rating" requirement. This
 * never touches the database and is display-only; it does not create or
 * imply any review record.
 */
export const getDefaultStarRating = (product) => {
  const h = hash(productKey(product));
  return Math.round((3 + (h % 11) / 10) * 10) / 10; // 3.0, 3.1, ... 4.0
};

/**
 * Rating + count used wherever a product's star rating is shown, so the
 * header average always matches the reviews actually listed below it.
 * `realAverage` / `realCount` are the values the API already gives us.
 *
 * When a product has no real (admin-approved) reviews yet, the average
 * shown is the static 3-4 star fallback above, instead of being pulled up
 * by the sample reviews' own (mostly 4-5 star) ratings - the sample review
 * cards themselves are unaffected and keep showing exactly as before.
 */
export const getProductRatingSummary = (product, realAverage, realCount) => {
  const rc = Number(realCount ?? product?.review_count ?? 0) || 0;
  const ra = Number(realAverage ?? product?.average_rating ?? 0) || 0;
  const merged = getProductReviewsWithSamples(
    product,
    Array.from({ length: rc }, () => ({ rating: ra }))
  );
  const total = merged.length;

  if (rc === 0) {
    return { average: getDefaultStarRating(product), count: total };
  }

  const sum = merged.reduce((acc, r) => acc + Number(r.rating || 0), 0);
  const average = total ? Math.round((sum / total) * 10) / 10 : 0;
  return { average, count: total };
};